import * as React from "react"
import {useContext, useState, useEffect} from "react"
import {AppContext} from "../index"
import ButtonList from "../components/common/buttonList"
import {Field, Formik} from "formik"
import {axiosInstance} from "../requests/axiosInstance"
import BackButton from "../components/common/backButton"
import SuccessOrFail from "../components/common/successOrFail"
import {isInternalDev, isInternalDevSandbox, isQa} from "../services/environment"
import {Button, Checkboxes, Fieldset, Form, Label, TextInput} from "nhsuk-react-components"

interface ConfigFormValues {
  useSigningMock: boolean
  useProxygen: boolean
  epsPrNumber: string
  signingPrNumber: string
  forceFameBackend: boolean
}

interface ConfigResponse {
  success: boolean
}

const ConfigPage: React.FC = () => {
  const {baseUrl, environment} = useContext(AppContext)
  const [configUpdateSuccess, setConfigUpdateSuccess] = useState(undefined)
  const [configValues, setConfigValues] = useState({useSigningMock: false, epsPrNumber: "", signingPrNumber: "", useProxygen: false, forceFameBackend: false})
  const [configLoaded, setConfigLoaded] = useState(false)
  const initialValues = {
    epsPrNumber: configValues.epsPrNumber,
    signingPrNumber: configValues.signingPrNumber,
    useProxygen: !!configValues.useProxygen,
    useSigningMock: !!configValues.useSigningMock,
    forceFameBackend: !!configValues.forceFameBackend,
  }
  const isDevOrQa = isInternalDev(environment) || isInternalDevSandbox(environment) || isQa(environment)

  useEffect(() => {
    axiosInstance.get(`${baseUrl}getconfig`)
      .then(response => {
        if (response.status === 200) {
          const configDetails = response.data
          if (configDetails.hasOwnProperty("useSigningMock") &&
            configDetails.hasOwnProperty("epsPrNumber") &&
            configDetails.hasOwnProperty("signingPrNumber") &&
            configDetails.hasOwnProperty("useProxygen") &&
            configDetails.hasOwnProperty("forceFameBackend")) {
            setConfigValues(response.data)
          }
        }
      })
      .finally(() => setConfigLoaded(true))
  }, [])

  if (configUpdateSuccess !== undefined) {
    return <>
      <Label isPageHeading>Config Saved {<SuccessOrFail condition={configUpdateSuccess} />}</Label>
      <ButtonList>
        <BackButton />
      </ButtonList>
    </>
  }

  if (!configLoaded) {
    return <>
      <Label isPageHeading>Config</Label>
      <Label>Loading...</Label>
    </>
  }

  return (
    <>
      <Label isPageHeading>Config</Label>
      <Formik<ConfigFormValues>
        initialValues={initialValues}
        onSubmit={values => updateConfig(baseUrl, values, setConfigUpdateSuccess)}
        enableReinitialize={true}>
        {formik =>
          <Form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <Label bold>EPS</Label>
            <Fieldset>
              {isDevOrQa &&
                <Field
                  id="epsPrNumber"
                  name="epsPrNumber"
                  as={TextInput}
                  width={30}
                  label="EPS PR Number"
                />
              }
              <Label bold>Use Proxygen</Label>
              <Checkboxes id="useProxygenCheckboxes">
                <Checkboxes.Item
                  id="useProxygen"
                  name="useProxygen"
                  checked={!!formik.values.useProxygen}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  Use Proxygen deployed APIs
                </Checkboxes.Item>
              </Checkboxes>
              {isDevOrQa &&
                <>
                  <Label bold>Signing</Label>
                  <Checkboxes id="useSigningMockCheckboxes">
                    <Checkboxes.Item
                      id="useSigningMock"
                      name="useSigningMock"
                      checked={!!formik.values.useSigningMock}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      Use Signing Mock
                    </Checkboxes.Item>
                  </Checkboxes>
                </>
              }
              {!formik.values.useSigningMock && isDevOrQa &&
                <Field
                  id="signingPrNumber"
                  name="signingPrNumber"
                  as={TextInput}
                  width={30}
                  label="Signing PR Number"
                />
              }
              <Label bold>FAME Backend</Label>
              <Checkboxes id="forceFameBackendCheckboxes">
                <Checkboxes.Item
                  id="forceFameBackend"
                  name="forceFameBackend"
                  checked={!!formik.values.forceFameBackend}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  Force FAME Backend
                </Checkboxes.Item>
              </Checkboxes>
            </Fieldset>
            <ButtonList>
              <Button type="submit">Save</Button>
            </ButtonList>
          </Form>
        }
      </Formik >
    </>
  )
}

async function updateConfig(
  baseUrl: string,
  configFormValues: ConfigFormValues,
  setConfigUpdateSuccess: React.Dispatch<boolean>
): Promise<void> {
  const success = (await axiosInstance.post<ConfigResponse>(`${baseUrl}config`, configFormValues)).data.success
  setConfigUpdateSuccess(success)
}

export default ConfigPage
